export type CropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TrimResult = {
  blob: Blob;
  mimeType: string;
  url: string;
};

type ExportClipOptions = {
  cropRect: CropRect;
  mimeType: string;
  startTime: number;
  video: HTMLVideoElement;
};

type CaptureCanvas = HTMLCanvasElement & {
  captureStream?: (frameRate?: number) => MediaStream;
};

export async function exportOneSecondClip({
  cropRect,
  mimeType,
  startTime,
  video
}: ExportClipOptions): Promise<TrimResult> {
  if (!mimeType) {
    throw new Error("当前浏览器没有可用 MediaRecorder 导出格式");
  }

  if (!Number.isFinite(video.duration) || video.duration <= 0) {
    throw new Error("视频元数据尚未加载完成");
  }

  const captureCanvas = document.createElement("canvas") as CaptureCanvas;
  if (typeof captureCanvas.captureStream !== "function") {
    throw new Error("当前 WebView 不支持 canvas.captureStream，无法前端导出裁剪视频");
  }

  const sourceWidth = video.videoWidth || 1;
  const sourceHeight = video.videoHeight || 1;
  const cropX = Math.round(cropRect.x * sourceWidth);
  const cropY = Math.round(cropRect.y * sourceHeight);
  const cropWidth = Math.max(2, Math.round(cropRect.width * sourceWidth));
  const cropHeight = Math.max(2, Math.round(cropRect.height * sourceHeight));
  const maxEdge = 720;
  const scale = Math.min(1, maxEdge / Math.max(cropWidth, cropHeight));

  captureCanvas.width = Math.max(2, Math.round(cropWidth * scale));
  captureCanvas.height = Math.max(2, Math.round(cropHeight * scale));

  const context = captureCanvas.getContext("2d");
  if (!context) {
    throw new Error("无法创建 Canvas 2D 上下文");
  }
  const drawContext = context;

  const stream = captureCanvas.captureStream(30);
  const recorder = new MediaRecorder(stream, { mimeType });
  const chunks: BlobPart[] = [];
  let animationFrame = 0;
  const previousTime = video.currentTime;
  const wasPaused = video.paused;
  const clipDuration = Math.min(1, Math.max(0.1, video.duration - startTime));

  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  await seekVideo(video, Math.min(startTime, Math.max(0, video.duration - 0.1)));
  video.muted = true;
  video.playsInline = true;

  const startedAt = performance.now();

  const stopPromise = new Promise<Blob>((resolve, reject) => {
    recorder.onerror = () => reject(new Error("MediaRecorder 导出过程失败"));
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      if (blob.size === 0) {
        reject(new Error("导出结果为空"));
        return;
      }
      resolve(blob);
    };
  });

  try {
    function stopRecorder() {
      if (recorder.state !== "inactive") {
        recorder.stop();
      }
    }

    function drawFrame() {
      try {
        drawContext.drawImage(
          video,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          captureCanvas.width,
          captureCanvas.height
        );
      } catch {
        stopRecorder();
        return;
      }

      animationFrame = window.requestAnimationFrame(drawFrame);
    }

    recorder.start(100);
    drawFrame();
    await video.play();

    await Promise.race([
      waitFor(() => {
        const elapsedByClock = (performance.now() - startedAt) / 1000;
        const elapsedByVideo = video.currentTime - startTime;
        return elapsedByClock >= clipDuration || elapsedByVideo >= clipDuration || video.ended;
      }),
      stopPromise.then(() => undefined),
    ]);

    stopRecorder();
    const blob = await stopPromise;

    return {
      blob,
      mimeType,
      url: URL.createObjectURL(blob)
    };
  } catch (error) {
    if (recorder.state !== "inactive") {
      try {
        recorder.stop();
        await stopPromise;
      } catch {
        // The original error below is more useful to the user than a cleanup error.
      }
    }
    throw error;
  } finally {
    if (animationFrame) {
      window.cancelAnimationFrame(animationFrame);
    }
    video.pause();
    stream.getTracks().forEach((track) => track.stop());

    try {
      await seekVideo(video, previousTime);
      if (!wasPaused) {
        await video.play();
      }
    } catch {
      // Restoring preview playback is best effort only.
    }
  }
}

function seekVideo(video: HTMLVideoElement, currentTime: number) {
  return new Promise<void>((resolve, reject) => {
    if (Math.abs(video.currentTime - currentTime) < 0.05 && video.readyState >= 2) {
      window.setTimeout(resolve, 0);
      return;
    }

    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("视频定位超时"));
    }, 8000);

    function cleanup() {
      window.clearTimeout(timeout);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("error", handleError);
    }

    function handleSeeked() {
      cleanup();
      resolve();
    }

    function handleError() {
      cleanup();
      reject(new Error("视频定位失败"));
    }

    video.addEventListener("seeked", handleSeeked, { once: true });
    video.addEventListener("error", handleError, { once: true });
    video.currentTime = currentTime;
  });
}

function waitFor(done: () => boolean) {
  return new Promise<void>((resolve) => {
    function tick() {
      if (done()) {
        resolve();
        return;
      }
      window.setTimeout(tick, 60);
    }
    tick();
  });
}
